// Searchable, server-paged picker for the large master lists (vendors ~2,700).
// Used in the Import Control edit modal for vendor / company / GL account.
// On pick it returns both the id and the canonical name (app.md §9 step 5).
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Combobox,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'

export interface EntityOption {
  id: string
  primary: string
  secondary?: string
}

interface Props {
  /** Current selection (id + name) if any. */
  value?: { id?: string; name?: string }
  search: (term: string) => Promise<EntityOption[]>
  onSelect: (option: EntityOption) => void
  placeholder?: string
  disabled?: boolean
  /** Match surrounding controls (e.g. small inputs in the line items table). */
  size?: 'small' | 'medium' | 'large'
  /**
   * Where to mount the listbox. Pass the enclosing `DialogSurface` element when
   * this picker lives in a dialog: the listbox then renders at the surface's own
   * level — above the form's fields rather than fighting them for stacking order
   * — while staying inside the dialog's focus trap. Omit it outside a dialog and
   * the listbox renders inline, which is correct there.
   */
  mountNode?: HTMLElement | null
}

// Empty-term ("top results") responses shared across pickers with the same
// search fn, so opening a many-line invoice doesn't fire one query per picker.
// Keyed weakly by the fn identity; entries drop when the fn is GC'd.
const emptyResultsCache = new WeakMap<Props['search'], EntityOption[]>()

const useStyles = makeStyles({
  hint: { padding: '6px 12px', color: tokens.colorNeutralForeground3 },
  // Fluent's Combobox defaults to ~250px min-width; clear it so the picker fills
  // its container (e.g. a line item's Account cell) instead of overrunning the next.
  // The root is an inline-grid of [input 1fr | chevron auto]; without min-width 0
  // the input can't shrink below its intrinsic width, which pushes the chevron
  // outside the root's border in narrow containers.
  combobox: {
    width: '100%',
    minWidth: 0,
    '& input': { minWidth: 0, width: '100%' },
  },
  // The listbox renders inline (inlinePopup, so it stays in the modal's focus
  // trap), which otherwise inherits the (narrow) Account/Company cell width and
  // truncates option text. Give it a comfortable min-width so names + codes are
  // readable; it overlays neighboring columns like any dropdown.
  //
  // The rest is legibility, reported from the Assignments dialog: an INLINE
  // listbox inherits its container's surface, so inside a dialog it blended into
  // the dialog and ran past the content box unbounded. An explicit raised
  // surface plus a bounded, self-scrolling height makes it read as a dropdown
  // wherever it opens.
  listbox: {
    minWidth: '340px',
    maxWidth: '92vw',
    // Owns the height, because `autoSize` is off in `positioning` below.
    maxHeight: '280px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    // High, not 1: in the inline case the listbox shares a stacking context with
    // the fields it overlays and with anything below it in the dialog, and a
    // z-index of 1 loses to any of them that is itself positioned. Reported as
    // "the z-index is off" — the options rendered, underneath.
    zIndex: 1000,
  },
  // Two lines per row need room to separate; Fluent's default option padding is
  // tuned for one.
  option: { paddingTop: '6px', paddingBottom: '6px' },
  optionPrimary: { color: tokens.colorNeutralForeground1 },
  optionSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

/**
 * Cast because the slot's type admits no `data-*` attributes, and these two are
 * the documented opt-outs for LastPass and Dashlane — attributes those
 * extensions read off the DOM, not React props with a declared type.
 */
function inputSlotProps(name: string): Record<string, unknown> {
  return {
    autoComplete: 'off',
    spellCheck: false,
    name,
    'data-lpignore': 'true',
    'data-form-type': 'other',
  }
}

export function EntityPicker({ value, search, onSelect, placeholder, disabled, size, mountNode }: Props) {
  const s = useStyles()
  // Defeats Chrome's field-name heuristic (see the `input` slot below): with a
  // per-instance nonsense name there is no "Person"-shaped field to match saved
  // form history against.
  const fieldName = `entity-search-${useId()}`
  const valueName = value?.name ?? ''
  const [query, setQuery] = useState(valueName)
  const [options, setOptions] = useState<EntityOption[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Only the latest search may write options — a slow earlier response (e.g. the
  // first open-term query) must not overwrite a newer one.
  const searchSeq = useRef(0)
  const didInitialFetch = useRef(false) // lazy: first fetch waits for focus/open
  const editing = useRef(false) // input focused — don't clobber in-progress typing
  const typedWithoutSelect = useRef(false) // freeform text not yet committed to a pick

  const runSearch = useCallback(
    (term: string) => {
      const my = ++searchSeq.current
      const cached = term === '' ? emptyResultsCache.get(search) : undefined
      if (cached) {
        setOptions(cached)
        return
      }
      setLoading(true)
      search(term)
        .then((opts) => {
          if (term === '') emptyResultsCache.set(search, opts)
          if (my === searchSeq.current) setOptions(opts)
        })
        .catch(() => { if (my === searchSeq.current) setOptions([]) })
        .finally(() => { if (my === searchSeq.current) setLoading(false) })
    },
    [search],
  )

  // Resync the displayed text when the selection changes upstream (e.g. a header
  // company change repoints inheriting line items) — but never while the user
  // is typing into this field.
  useEffect(() => {
    if (!editing.current) setQuery(valueName)
  }, [valueName])

  // Clear a pending debounce on unmount.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  function ensureInitialFetch() {
    if (didInitialFetch.current) return
    didInitialFetch.current = true
    runSearch('')
  }

  function onInput(text: string) {
    setQuery(text)
    typedWithoutSelect.current = true
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => runSearch(text), 300)
  }

  return (
    <Combobox
      // Keep the listbox inside the Import Control modal's focus trap. Portaled
      // to body (the default) it renders outside the Dialog's DOM, so the trap
      // reclaims focus and slams the field shut the instant you engage it —
      // intermittently, since it only bites when a re-render (e.g. the 90s badge
      // poll) or the async open race lands mid-interaction. Same fix the sibling
      // Dropdown and AssociatedEntitiesEditor already carry.
      // The BROWSER's own autofill dropdown, suppressed. Fluent sets no
      // `autocomplete` on the trigger input, so Chrome treats a field labelled
      // "Person" with placeholder "Search staff…" as a name field and offers its
      // saved form history, over the top of the real listbox. `autocomplete=off`
      // alone is advisory for name-shaped fields, so the per-instance `name`
      // removes the shape as well; the two `data-` attributes are the documented
      // opt-outs for LastPass and Dashlane. NOT `new-password`, which suppresses
      // reliably but makes Chrome offer to generate a password in a staff search.
      input={inputSlotProps(fieldName)}
      // Inline ONLY when there is no mount node: a portal to document.body would
      // sit outside a dialog's focus trap and tabster would haul focus back,
      // slamming the listbox shut (app/README.md). `mountNode` gets both — the
      // dialog's trap AND a layer above the dialog's own fields.
      {...(mountNode ? { mountNode } : { inlinePopup: true })}
      // Positioning has to be stated because `inlinePopup` changes what the
      // popup is measured against. Left to itself it takes its clipping box from
      // the nearest clipping ancestor — inside a Dialog that is the dialog body,
      // a couple of fields tall — so `autoSize` shrank the listbox to a single
      // scrolling row and flipped it ABOVE the input, over the field label
      // (reported from Assignments, 2026-08-06; screenshot confirmed).
      //
      // Measure against the window instead, and turn `autoSize` off so the
      // height comes from the stylesheet (`s.listbox`) rather than from whatever
      // space the dialog happens to leave.
      positioning={{
        position: 'below',
        align: 'start',
        autoSize: false,
        flipBoundary: 'window',
        overflowBoundary: 'window',
      }}
      listbox={{ className: s.listbox }}
      className={s.combobox}
      size={size}
      disabled={disabled}
      placeholder={placeholder ?? 'Search…'}
      value={query}
      selectedOptions={value?.id ? [value.id] : []}
      onFocus={() => { editing.current = true; ensureInitialFetch() }}
      onOpenChange={(_, data) => { if (data.open) ensureInitialFetch() }}
      onBlur={() => {
        editing.current = false
        // Freeform text the user typed but never committed to a pick saves
        // nothing — revert to the current selection so the field only ever shows
        // text that will be persisted.
        if (typedWithoutSelect.current) {
          typedWithoutSelect.current = false
          setQuery(valueName)
        }
      }}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      onOptionSelect={(_, data) => {
        const opt = options.find((o) => o.id === data.optionValue)
        if (opt) {
          typedWithoutSelect.current = false
          setQuery(opt.primary)
          onSelect(opt)
        }
      }}
      freeform
    >
      {loading && (
        <div className={s.hint}>
          <Spinner size="tiny" label="Searching…" />
        </div>
      )}
      {!loading && options.length === 0 && (
        <div className={s.hint}>No matches.</div>
      )}
      {options.map((o) => (
        <Option key={o.id} value={o.id} text={o.primary} className={s.option}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className={s.optionPrimary}>{o.primary}</span>
            {o.secondary && <span className={s.optionSecondary}>{o.secondary}</span>}
          </div>
        </Option>
      ))}
    </Combobox>
  )
}
